import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setSystemPreference: () => void;
  isUsingSystemPreference: boolean;
  setTheme: (theme: Theme) => void;
}

interface StoredThemeData {
  theme: Theme;
  expiry: number;
}

const THEME_STORAGE_KEY = 'srcful-theme';
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  // Check if there's a stored theme with expiration
  const getStoredThemeData = (): StoredThemeData | null => {
    const storedData = localStorage.getItem(THEME_STORAGE_KEY);
    if (!storedData) return null;
    
    try {
      const parsedData = JSON.parse(storedData) as StoredThemeData;
      // Check if the stored preference has expired
      if (parsedData.expiry && parsedData.expiry > Date.now()) {
        return parsedData;
      }
    } catch (e) {
      // Invalid JSON, clear it
      localStorage.removeItem(THEME_STORAGE_KEY);
    }
    return null;
  };

  const [isUsingSystemPreference, setIsUsingSystemPreference] = useState<boolean>(() => {
    return !getStoredThemeData();
  });
  
  // Initialize theme from localStorage or system preference
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first with expiration
    const storedData = getStoredThemeData();
    if (storedData) {
      return storedData.theme;
    }
    
    // Default to dark theme instead of using system preference
    return 'dark';
  });

  // Save theme to localStorage with expiration
  const saveThemeToStorage = (newTheme: Theme) => {
    const themeData: StoredThemeData = {
      theme: newTheme,
      expiry: Date.now() + ONE_YEAR_MS
    };
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(themeData));
  };

  // Function to switch to system preference
  const setSystemPreference = () => {
    // Remove theme from localStorage
    localStorage.removeItem(THEME_STORAGE_KEY);
    setIsUsingSystemPreference(true);
    
    // Set theme based on system preference
    const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setTheme(prefersDarkMode ? 'dark' : 'light');
  };

  // Function to directly set the theme
  const setThemeValue = (newTheme: Theme) => {
    setTheme(newTheme);
    setIsUsingSystemPreference(false);
    saveThemeToStorage(newTheme);
  };

  // Update document attributes when theme changes
  useEffect(() => {
    // We don't need to assign 'root' if we're not using it
    
    if (theme === 'light') {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
      document.body.classList.add('light');
      document.body.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    }
    
    // Save to localStorage only if not using system preference
    if (!isUsingSystemPreference) {
      saveThemeToStorage(theme);
    }
  }, [theme, isUsingSystemPreference]);

  // Add listener for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if we're using system preference
      if (isUsingSystemPreference) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    // Modern browsers
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [isUsingSystemPreference]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    setIsUsingSystemPreference(false);
    saveThemeToStorage(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      toggleTheme, 
      setSystemPreference, 
      isUsingSystemPreference,
      setTheme: setThemeValue 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 